import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ListMusic } from "lucide-react";

const empty = {
  client_name: "", event_date: "", must_play: "", do_not_play: "",
  first_dance: "", spotify_link: "", apple_link: "", notes: "",
};

const ClientPlaylist = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.event_date) {
      toast.error("Client name and event date are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/playlists", form);
      setDone(true);
      toast.success("Playlist submitted!");
      setForm(empty);
    } catch {
      toast.error("Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="playlist-success">
        <div className="text-center max-w-xl">
          <p className="eyebrow">Playlist received</p>
          <h1 className="font-heading text-5xl md:text-6xl uppercase mt-4 leading-[0.95]">Loud and clear.</h1>
          <p className="mt-6 text-neutral-300 text-lg">
            We've got your music. The band will review and reach out if we have any questions before the show.
          </p>
          <button onClick={() => setDone(false)} className="btn-retro mt-10" data-testid="playlist-another">Submit another</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="playlist-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="eyebrow fade-up"><ListMusic size={12} className="inline mr-2" /> Booked Client Playlist</p>
          <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="playlist-title">
            Your Soundtrack
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
            Send us the songs that matter. Must-plays, do-not-plays, first dance, hype tracks — we'll build the night around it.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <form onSubmit={submit} className="space-y-10" data-testid="playlist-form">
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <label className="label-retro">Client name *</label>
                <input value={form.client_name} onChange={onChange("client_name")} className="field" required data-testid="pl-name" />
              </div>
              <div>
                <label className="label-retro">Event date *</label>
                <input type="date" value={form.event_date} onChange={onChange("event_date")} className="field [color-scheme:dark]" required data-testid="pl-date" />
              </div>
            </div>

            <div>
              <label className="label-retro">Must-play songs</label>
              <textarea value={form.must_play} onChange={onChange("must_play")} className="field-area" placeholder="One per line — artist – song" data-testid="pl-mustplay" />
            </div>
            <div>
              <label className="label-retro">Do-not-play songs</label>
              <textarea value={form.do_not_play} onChange={onChange("do_not_play")} className="field-area" placeholder="One per line" data-testid="pl-donotplay" />
            </div>
            <div>
              <label className="label-retro">First dance / special songs</label>
              <textarea value={form.first_dance} onChange={onChange("first_dance")} className="field-area" placeholder="First dance, parent dances, anniversary song..." data-testid="pl-firstdance" />
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <label className="label-retro">Spotify playlist link</label>
                <input value={form.spotify_link} onChange={onChange("spotify_link")} className="field" placeholder="https://open.spotify.com/playlist/..." data-testid="pl-spotify" />
              </div>
              <div>
                <label className="label-retro">Apple Music playlist link</label>
                <input value={form.apple_link} onChange={onChange("apple_link")} className="field" placeholder="https://music.apple.com/..." data-testid="pl-apple" />
              </div>
            </div>

            <div>
              <label className="label-retro">Notes for the band</label>
              <textarea value={form.notes} onChange={onChange("notes")} className="field-area" placeholder="Vibe, energy, dress code, anything else..." data-testid="pl-notes" />
            </div>

            <div className="border-t border-white/10 pt-6 flex items-center justify-end flex-wrap gap-4">
              <button disabled={submitting} type="submit" className="btn-retro-solid disabled:opacity-50" data-testid="pl-submit">
                {submitting ? "Sending..." : "Submit Playlist"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ClientPlaylist;
